/**
 * UTC-aware wrapper for date-fns functions.
 *
 * This module provides wrappers around date-fns functions that automatically
 * use UTC timezone context. This eliminates the need to manually pass
 * `{ in: utc }` to every date-fns function call.
 *
 * @module @/utils/dates
 * @see https://date-fns.org/docs/Time-Zones
 *
 * @example
 * ```typescript
 * // ❌ Old way - error prone, easy to forget { in: utc }
 * import { startOfMonth } from 'date-fns';
 * import { utc } from '@date-fns/utc';
 * const start = startOfMonth(new UTCDate(), { in: utc });
 *
 * // ✅ New way - UTC context automatically applied
 * import { startOfMonth } from '@/utils/dates';
 * const start = startOfMonth(new UTCDate());
 * ```
 */

/* eslint-disable no-restricted-imports */
import { utc } from '@date-fns/utc';
import * as dateFns from 'date-fns';

// Re-export UTCDate for convenience
export { UTCDate } from '@date-fns/utc';

// Formatting functions
export function format(
  ...[date, formatStr, options]: Parameters<typeof dateFns.format>
): ReturnType<typeof dateFns.format> {
  return dateFns.format(date, formatStr, { ...options, in: utc });
}

export function formatISO(
  ...[date, options]: Parameters<typeof dateFns.formatISO>
): ReturnType<typeof dateFns.formatISO> {
  return dateFns.formatISO(date, { ...options, in: utc });
}

export function formatDistanceToNow(
  ...[date, options]: Parameters<typeof dateFns.formatDistanceToNow>
): ReturnType<typeof dateFns.formatDistanceToNow> {
  return dateFns.formatDistanceToNow(date, { ...options, in: utc });
}

export function formatDistanceToNowStrict(
  ...[date, options]: Parameters<typeof dateFns.formatDistanceToNowStrict>
): ReturnType<typeof dateFns.formatDistanceToNowStrict> {
  return dateFns.formatDistanceToNowStrict(date, { ...options, in: utc });
}

// Parsing functions
export function parse(
  ...[dateStr, formatStr, referenceDate, options]: Parameters<typeof dateFns.parse>
): ReturnType<typeof dateFns.parse> {
  return dateFns.parse(dateStr, formatStr, referenceDate, { ...options, in: utc });
}

export function parseISO(
  ...[dateString, options]: Parameters<typeof dateFns.parseISO>
): ReturnType<typeof dateFns.parseISO> {
  return dateFns.parseISO(dateString, { ...options, in: utc });
}

// Month operations
export function startOfMonth(
  ...[date, options]: Parameters<typeof dateFns.startOfMonth>
): ReturnType<typeof dateFns.startOfMonth> {
  return dateFns.startOfMonth(date, { ...options, in: utc });
}

export function endOfMonth(
  ...[date, options]: Parameters<typeof dateFns.endOfMonth>
): ReturnType<typeof dateFns.endOfMonth> {
  return dateFns.endOfMonth(date, { ...options, in: utc });
}

export function addMonths(
  ...[date, amount, options]: Parameters<typeof dateFns.addMonths>
): ReturnType<typeof dateFns.addMonths> {
  return dateFns.addMonths(date, amount, { ...options, in: utc });
}

export function subMonths(
  ...[date, amount, options]: Parameters<typeof dateFns.subMonths>
): ReturnType<typeof dateFns.subMonths> {
  return dateFns.subMonths(date, amount, { ...options, in: utc });
}

export function isSameMonth(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.isSameMonth>
): ReturnType<typeof dateFns.isSameMonth> {
  return dateFns.isSameMonth(dateLeft, dateRight, { ...options, in: utc });
}

export function differenceInMonths(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.differenceInMonths>
): ReturnType<typeof dateFns.differenceInMonths> {
  return dateFns.differenceInMonths(dateLeft, dateRight, { ...options, in: utc });
}

export function differenceInCalendarMonths(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.differenceInCalendarMonths>
): ReturnType<typeof dateFns.differenceInCalendarMonths> {
  return dateFns.differenceInCalendarMonths(dateLeft, dateRight, { ...options, in: utc });
}

export function eachMonthOfInterval(
  ...[interval, options]: Parameters<typeof dateFns.eachMonthOfInterval>
): ReturnType<typeof dateFns.eachMonthOfInterval> {
  return dateFns.eachMonthOfInterval(interval, { ...options, in: utc });
}

// Day operations
export function startOfDay(
  ...[date, options]: Parameters<typeof dateFns.startOfDay>
): ReturnType<typeof dateFns.startOfDay> {
  return dateFns.startOfDay(date, { ...options, in: utc });
}

export function endOfDay(...[date, options]: Parameters<typeof dateFns.endOfDay>): ReturnType<typeof dateFns.endOfDay> {
  return dateFns.endOfDay(date, { ...options, in: utc });
}

export function addDays(
  ...[date, amount, options]: Parameters<typeof dateFns.addDays>
): ReturnType<typeof dateFns.addDays> {
  return dateFns.addDays(date, amount, { ...options, in: utc });
}

export function subDays(
  ...[date, amount, options]: Parameters<typeof dateFns.subDays>
): ReturnType<typeof dateFns.subDays> {
  return dateFns.subDays(date, amount, { ...options, in: utc });
}

// Week operations
export function startOfWeek(
  ...[date, options]: Parameters<typeof dateFns.startOfWeek>
): ReturnType<typeof dateFns.startOfWeek> {
  return dateFns.startOfWeek(date, { ...options, in: utc });
}

export function endOfWeek(
  ...[date, options]: Parameters<typeof dateFns.endOfWeek>
): ReturnType<typeof dateFns.endOfWeek> {
  return dateFns.endOfWeek(date, { ...options, in: utc });
}

export function addWeeks(
  ...[date, amount, options]: Parameters<typeof dateFns.addWeeks>
): ReturnType<typeof dateFns.addWeeks> {
  return dateFns.addWeeks(date, amount, { ...options, in: utc });
}

export function subWeeks(
  ...[date, amount, options]: Parameters<typeof dateFns.subWeeks>
): ReturnType<typeof dateFns.subWeeks> {
  return dateFns.subWeeks(date, amount, { ...options, in: utc });
}

export function differenceInWeeks(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.differenceInWeeks>
): ReturnType<typeof dateFns.differenceInWeeks> {
  return dateFns.differenceInWeeks(dateLeft, dateRight, { ...options, in: utc });
}

// Year operations
export function startOfYear(
  ...[date, options]: Parameters<typeof dateFns.startOfYear>
): ReturnType<typeof dateFns.startOfYear> {
  return dateFns.startOfYear(date, { ...options, in: utc });
}

export function endOfYear(
  ...[date, options]: Parameters<typeof dateFns.endOfYear>
): ReturnType<typeof dateFns.endOfYear> {
  return dateFns.endOfYear(date, { ...options, in: utc });
}

export function subYears(
  ...[date, amount, options]: Parameters<typeof dateFns.subYears>
): ReturnType<typeof dateFns.subYears> {
  return dateFns.subYears(date, amount, { ...options, in: utc });
}

export function addYears(
  ...[date, amount, options]: Parameters<typeof dateFns.addYears>
): ReturnType<typeof dateFns.addYears> {
  return dateFns.addYears(date, amount, { ...options, in: utc });
}

export function differenceInYears(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.differenceInYears>
): ReturnType<typeof dateFns.differenceInYears> {
  return dateFns.differenceInYears(dateLeft, dateRight, { ...options, in: utc });
}

export function eachYearOfInterval(
  ...[interval, options]: Parameters<typeof dateFns.eachYearOfInterval>
): ReturnType<typeof dateFns.eachYearOfInterval> {
  return dateFns.eachYearOfInterval(interval, { ...options, in: utc });
}

export function getYear(...[date, options]: Parameters<typeof dateFns.getYear>): ReturnType<typeof dateFns.getYear> {
  return dateFns.getYear(date, { ...options, in: utc });
}

export function isSameYear(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.isSameYear>
): ReturnType<typeof dateFns.isSameYear> {
  return dateFns.isSameYear(dateLeft, dateRight, { ...options, in: utc });
}

export function isThisYear(
  ...[date, options]: Parameters<typeof dateFns.isThisYear>
): ReturnType<typeof dateFns.isThisYear> {
  return dateFns.isThisYear(date, { ...options, in: utc });
}

// Time operations
export function setHours(
  ...[date, hours, options]: Parameters<typeof dateFns.setHours>
): ReturnType<typeof dateFns.setHours> {
  return dateFns.setHours(date, hours, { ...options, in: utc });
}

export function addHours(
  ...[date, amount, options]: Parameters<typeof dateFns.addHours>
): ReturnType<typeof dateFns.addHours> {
  return dateFns.addHours(date, amount, { ...options, in: utc });
}

export function subHours(
  ...[date, amount, options]: Parameters<typeof dateFns.subHours>
): ReturnType<typeof dateFns.subHours> {
  return dateFns.subHours(date, amount, { ...options, in: utc });
}

export function getTime(...[date]: Parameters<typeof dateFns.getTime>): ReturnType<typeof dateFns.getTime> {
  return dateFns.getTime(date);
}

// Date getters
export function getMonth(...[date, options]: Parameters<typeof dateFns.getMonth>): ReturnType<typeof dateFns.getMonth> {
  return dateFns.getMonth(date, { ...options, in: utc });
}

export function getDate(...[date, options]: Parameters<typeof dateFns.getDate>): ReturnType<typeof dateFns.getDate> {
  return dateFns.getDate(date, { ...options, in: utc });
}

// Comparison functions
export function isAfter(
  ...[date, dateToCompare]: Parameters<typeof dateFns.isAfter>
): ReturnType<typeof dateFns.isAfter> {
  return dateFns.isAfter(date, dateToCompare);
}

export function isBefore(
  ...[date, dateToCompare]: Parameters<typeof dateFns.isBefore>
): ReturnType<typeof dateFns.isBefore> {
  return dateFns.isBefore(date, dateToCompare);
}

export function isSameDay(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.isSameDay>
): ReturnType<typeof dateFns.isSameDay> {
  return dateFns.isSameDay(dateLeft, dateRight, { ...options, in: utc });
}

export function isFuture(...[date]: Parameters<typeof dateFns.isFuture>): ReturnType<typeof dateFns.isFuture> {
  return dateFns.isFuture(date);
}

export function isValid(...[date]: Parameters<typeof dateFns.isValid>): ReturnType<typeof dateFns.isValid> {
  return dateFns.isValid(date);
}

// Today/Yesterday/Tomorrow checks
export function isToday(...[date, options]: Parameters<typeof dateFns.isToday>): ReturnType<typeof dateFns.isToday> {
  return dateFns.isToday(date, { ...options, in: utc });
}

export function isYesterday(
  ...[date, options]: Parameters<typeof dateFns.isYesterday>
): ReturnType<typeof dateFns.isYesterday> {
  return dateFns.isYesterday(date, { ...options, in: utc });
}

export function isTomorrow(
  ...[date, options]: Parameters<typeof dateFns.isTomorrow>
): ReturnType<typeof dateFns.isTomorrow> {
  return dateFns.isTomorrow(date, { ...options, in: utc });
}

// Interval operations
export function isWithinInterval(
  ...[date, interval, options]: Parameters<typeof dateFns.isWithinInterval>
): ReturnType<typeof dateFns.isWithinInterval> {
  return dateFns.isWithinInterval(date, interval, { ...options, in: utc });
}

export function differenceInDays(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.differenceInDays>
): ReturnType<typeof dateFns.differenceInDays> {
  return dateFns.differenceInDays(dateLeft, dateRight, { ...options, in: utc });
}

// Quarter operations
export function startOfQuarter(
  ...[date, options]: Parameters<typeof dateFns.startOfQuarter>
): ReturnType<typeof dateFns.startOfQuarter> {
  return dateFns.startOfQuarter(date, { ...options, in: utc });
}

export function endOfQuarter(
  ...[date, options]: Parameters<typeof dateFns.endOfQuarter>
): ReturnType<typeof dateFns.endOfQuarter> {
  return dateFns.endOfQuarter(date, { ...options, in: utc });
}

export function addQuarters(
  ...[date, amount, options]: Parameters<typeof dateFns.addQuarters>
): ReturnType<typeof dateFns.addQuarters> {
  return dateFns.addQuarters(date, amount, { ...options, in: utc });
}

export function differenceInQuarters(
  ...[dateLeft, dateRight, options]: Parameters<typeof dateFns.differenceInQuarters>
): ReturnType<typeof dateFns.differenceInQuarters> {
  return dateFns.differenceInQuarters(dateLeft, dateRight, { ...options, in: utc });
}

// Utility functions
export function min(...[dates, options]: Parameters<typeof dateFns.min>): ReturnType<typeof dateFns.min> {
  return dateFns.min(dates, { ...options, in: utc });
}

export function max(...[dates, options]: Parameters<typeof dateFns.max>): ReturnType<typeof dateFns.max> {
  return dateFns.max(dates, { ...options, in: utc });
}
